import { calcularMontosCobertura } from "../../utils/copagoCalculo";

/* ============================================================================
   SEGUROS-PLANES FASE 4 · las reglas del constructor, sin React.

   Aquí vive todo lo que se puede equivocar de verdad: qué se puede seleccionar,
   qué se envía al backend y qué se considera un cambio. Separado del componente
   a propósito, porque es lo que hay que poder probar sin montar una pantalla.

   NADA DE ESTO INVENTA REGLAS. Las tres que se aplican ya existen en el
   sistema y están verificadas en el informe de la FASE 0:

   1. `planes_examenes` tiene PK compuesta `(id_plan, id_tipo_examen)`. Por
      tanto un plan NO puede contener dos filas del tarifario que apunten al
      mismo `ExamenTipo`. Si la UI dejara marcar la segunda, el guardado
      reventaría —o peor, el upsert pisaría la primera en silencio.

   2. `precio_paciente` y `precio_empresa` son DERIVADOS de (copago, tarifa).
      El backend los recalcula e ignora lo que mande el frontend
      (FCopagoCalculo.ts). Se envían igual porque el schema los declara, pero
      calculados con el mismo espejo que usa el resto del sistema.

   3. Las validaciones al guardar son EXACTAMENTE las de la pantalla vieja:
      nombre obligatorio, al menos un procedimiento, y ningún copago vacío.
      Ni una más, ni una menos.
   ========================================================================== */

/** Identidad de una fila del tarifario en la selección. */
export const idFila = (fila) => Number(fila.idprocedimiento_company_precios);

/** El `ExamenTipo` al que apunta una fila del tarifario. */
export const idExamen = (fila) =>
    Number(fila.idExamenTipo ?? fila.ExamenTipo?.IdExamenTipo ?? fila.ExamenTipo?.idExamenTipo);

/**
 * Qué `ExamenTipo` están ya ocupados, y por qué fila del tarifario.
 * Es lo que hace cumplir la regla 1 sin consultar nada.
 */
export function examenesOcupados(puestos, filasPorId) {
    const mapa = new Map();
    for (const id of puestos.keys()) {
        const fila = filasPorId.get(id);
        if (!fila) continue;
        const examen = idExamen(fila);
        if (Number.isFinite(examen)) mapa.set(examen, id);
    }
    return mapa;
}

/**
 * ¿Se puede meter esta fila en el plan?
 * No, si otra fila YA puesta ocupa su mismo `ExamenTipo` (regla 1).
 */
export function bloqueadaPor(fila, puestos, ocupados) {
    const id = idFila(fila);
    if (puestos.has(id)) return null; // ya está dentro: no está bloqueada
    const duenio = ocupados.get(idExamen(fila));
    return duenio === undefined || duenio === id ? null : duenio;
}

/** Filtrado del catálogo: texto (código o descripción) y categoría. */
export function filtrarCatalogo(catalogo, { texto = "", categoria = "" } = {}) {
    const buscado = texto.trim().toLocaleLowerCase("es");
    const cat = categoria === "" || categoria === null ? null : Number(categoria);

    return catalogo.filter((fila) => {
        if (cat !== null && Number(fila.ExamenTipo?.categoria_id) !== cat) return false;
        if (!buscado) return true;
        const codigo = String(fila.ExamenTipo?.codigo || "").toLocaleLowerCase("es");
        const desc = String(fila.ExamenTipo?.descripcion || "").toLocaleLowerCase("es");
        return codigo.includes(buscado) || desc.includes(buscado);
    });
}

/**
 * Añade al plan TODAS las filas del filtro que se puedan añadir.
 *
 * Actúa sobre el resultado del filtro completo, NO sobre lo visible en
 * pantalla: filtrar Laboratorio y pulsar "Agregar los 127" tiene que agregar
 * 127, no las diez de la primera pantalla. Con el catálogo cargado entero esto
 * es filtrar un array, sin ninguna petición.
 *
 * Devuelve también cuántas se saltaron por la regla 1, para poder decirlo en
 * vez de que desaparezcan sin explicación.
 */
export function agregarTodas(puestos, filas, copagoPorDefecto, filasPorId) {
    const nuevos = new Map(puestos);
    const ocupados = examenesOcupados(nuevos, filasPorId);
    let agregadas = 0;
    let saltadas = 0;

    for (const fila of filas) {
        const id = idFila(fila);
        if (nuevos.has(id)) continue;
        const examen = idExamen(fila);
        if (ocupados.has(examen)) {
            saltadas += 1;
            continue;
        }
        nuevos.set(id, copagoPorDefecto);
        ocupados.set(examen, id);
        agregadas += 1;
    }
    return { puestos: nuevos, agregadas, saltadas };
}

/** Quita del plan todas las filas del filtro que estuvieran dentro. */
export function quitarTodas(puestos, filas) {
    const nuevos = new Map(puestos);
    let quitadas = 0;
    for (const fila of filas) {
        if (nuevos.delete(idFila(fila))) quitadas += 1;
    }
    return { puestos: nuevos, quitadas };
}

/** Cuántas de `filas` están ya dentro del plan. */
export function cuantasPuestas(puestos, filas) {
    let n = 0;
    for (const fila of filas) if (puestos.has(idFila(fila))) n += 1;
    return n;
}

/**
 * Un copago es válido si es un número entre 0 y 100.
 *
 * El vacío se trata aparte (`copagosVacios`) porque es el único caso que la
 * pantalla vieja ya rechazaba explícitamente, con ese mismo mensaje.
 */
export function copagoValido(valor) {
    if (valor === null || valor === undefined || String(valor).trim() === "") return false;
    const n = Number(valor);
    return Number.isFinite(n) && n >= 0 && n <= 100;
}

/** Ids con el copago sin rellenar o fuera de rango. */
export function copagosInvalidos(puestos) {
    const malos = [];
    for (const [id, copago] of puestos) if (!copagoValido(copago)) malos.push(id);
    return malos;
}

/**
 * Resumen del pie del panel derecho: cuántos, copago promedio y categorías.
 * El promedio es una media simple de porcentajes, igual que en el listado.
 */
export function resumenPlan(puestos, filasPorId) {
    const ids = [...puestos.keys()];
    const categorias = new Set();
    let suma = 0;
    let conCopago = 0;

    for (const id of ids) {
        const fila = filasPorId.get(id);
        if (fila?.ExamenTipo?.categoria_id !== undefined) {
            categorias.add(Number(fila.ExamenTipo.categoria_id));
        }
        const c = Number(puestos.get(id));
        if (Number.isFinite(c)) {
            suma += c;
            conCopago += 1;
        }
    }

    return {
        procedimientos: ids.length,
        copagoPromedio: conCopago ? suma / conCopago : null,
        categorias: categorias.size,
    };
}

/**
 * El `detalle` que viaja a POST /planes y PUT /planes/:id.
 *
 * Misma forma exacta que envía la pantalla vieja: id_tipo_examen, copago,
 * precio_paciente, precio_empresa e idprocedimiento_company_precios. Un plan
 * creado por aquí produce las mismas filas en base de datos que uno creado por
 * el modal de siempre.
 */
export function construirDetalle(puestos, filasPorId) {
    const detalle = [];
    for (const [id, copago] of puestos) {
        const fila = filasPorId.get(id);
        if (!fila) continue; // sin la fila no hay tarifa: no se puede enviar
        const montos = calcularMontosCobertura(Number(fila.precio), Number(copago));
        detalle.push({
            id_tipo_examen: idExamen(fila),
            copago: Number(copago),
            precio_paciente: montos.precioPaciente,
            precio_empresa: montos.precioEmpresa,
            idprocedimiento_company_precios: id,
        });
    }
    return detalle;
}

/**
 * Qué líneas hay que dar de baja al actualizar: las que estaban en el plan
 * original y ya no están. El backend las espera por
 * `idprocedimiento_company_precios`, no por `id_tipo_examen`.
 */
export function construirEliminar(idsOriginales, puestos) {
    return idsOriginales.filter((id) => !puestos.has(id));
}

/**
 * ¿Cambió algo respecto de lo cargado? Gobierna si "Guardar cambios" se puede
 * pulsar. Compara nombre y el par (id, copago) de cada línea.
 */
export function hayCambios(original, actual) {
    if (!original) return true; // alta: siempre hay algo que guardar
    if (String(original.nombre || "").trim() !== String(actual.nombre || "").trim()) return true;
    if (original.puestos.size !== actual.puestos.size) return true;
    for (const [id, copago] of actual.puestos) {
        if (!original.puestos.has(id)) return true;
        if (Number(original.puestos.get(id)) !== Number(copago)) return true;
    }
    return false;
}
